import React, { useState, useContext, useEffect } from 'react'
import styled, { css } from 'styled-components'
import loadable from '@loadable/component'
import { LayoutContext, useUI } from '../../contexts'
import ReviewStarsProduct from '../shared/ReviewStarsProduct'
import Accordion from '../shared/Accordion'
import ProductForm from './ProductForm'
import { SiteLink } from '../shared/SiteLink'
import Swiper from 'react-id-swiper'
import 'swiper/css/swiper.css'
import { ImageZoom } from './ImageZoom'
import Bundle from './Bundle'
import { useIsMobile } from '../../hooks/useIsMobile'
import {
  useColorSelectedTracking,
  useProductZoomClickedTracking,
  useProductInfoClickedTracking,
  useReviewAnchorClickedTracking,
} from '../../hooks/tracking'
import { desktopVW, mobileVW } from '../../styles'
import ProductLabelLogo from './ProductLabelLogo'
import { getAvailable, getDiscountPercentage } from '../../utils'
import { useGlobal } from '../../hooks/useGlobal'

import Image from 'gatsby-image'
import BreadCrumb from '../shared/BreadCrumb'
import DynamicLabel from '../shared/DynamicLabel'
import theme from '../../styles/theme'
import AmazonButton from './AmazonButton'
import useShopifyProduct from '../../hooks/useShopifyProduct'
import RichText from '../RichText'

const { colors, breakPoints } = theme

const SizeTabel = loadable(() => import('./SizeTabel'))

const ProductItem = ({
  location,
  name,
  title,
  product,
  shopifyProduct,
  shopifyProduct2,
  productSlug,
  images,
  color,
  family,
  onViewReviews,
  onViewDescription,
  onToTop,
  gender,
  sizeSelect,
  productType,
  shortenedDescription,
  productBullets,
  label,
  labelTopRight,
  labelTopRightImage,
  labelTopRightText,
  labelBottomRight,
  labelBottomRightImage,
  labelBottomRightText,
  outOfStockMessages,
  relatedProducts,
  productBundle,
  productAccordions,
  thumbnailImage,
  notComingBack,
  position,
  amazonUrl,
  amazonPriceValue,
  description,
  logo,
  sizeInfo,
  removeIndividualTieredDiscountMessage,
  multipackProductInfo,
  preorder,
  preorderMessage,
}) => {
  const isSkiing = family?.name.toLowerCase().includes('skiing')

  //logic to have multi color to end of color list
  const sortedProductsByName = family.product
    .sort(function (a, b) {
      const nameA = a.color.color.length
      const nameB = b.color.color.length

      if (nameA < nameB) {
        return -1
      }
      if (nameA > nameB) {
        return 1
      }

      return 0
    })
    .filter(getAvailable)

  if (!gender) {
    gender = {}
  }

  const { node_locale, currency, storeSubdomain } = useContext(LayoutContext)

  let {
    layout: { home, backToTopText },
    product: {
      color: colorText,
      nextProduct,
      showProductPageBulletsHeading,
      showProductBundle,
    },
    blackFriday: {
      showAnnouncementBarBlackFriday2022,
      pDPBlackFridayLabelLineOne2022,
      pDPBlackFridayLabelLineTwo2022,
    },
  } = useGlobal(['layout', 'product', 'blackFriday'], node_locale)

  const isMobile = useIsMobile()
  const [discountPrice, setDiscountPrice] = useState('')
  const [originalPrice, setOriginalPrice] = useState('')
  const [selectedImage, setSelectedImage] = useState(0)
  const [toTopVisible, setToTopVisible] = useState(false)
  const [sizeTableOpen, setSizeTableOpen] = useState(false)
  const [swiper, updateSwiper] = useState(null)
  const [swiperIndex, setSwiperIndex] = useState(0)
  const [imageZoom, setImageZoom] = useState({ isOpen: false, image: null })
  const relatedProduct = relatedProducts?.find(getAvailable)
  const [variants, setVariants] = useState([])
  const [selectedVariant, setSelectedVariant] = useState()
  const [sizeIndex, setSizeIndex] = useState(0)

  let currentProduct
  const { product: product2 } = useShopifyProduct(shopifyProduct2?.handle)

  currentProduct = product ?? product2

  useEffect(() => {
    if (swiper !== null) {
      swiper.on('slideChange', () => {
        setSwiperIndex(swiper.realIndex)
        setSelectedImage(swiper.realIndex)
      })
    }

    const updateToTopVisible = () => {
      if (window.scrollY <= window.innerHeight) {
        setToTopVisible(false)
      } else {
        setToTopVisible(true)
      }
    }

    window.addEventListener('scroll', updateToTopVisible)

    setTimeout(() => {
      updateToTopVisible()
    }, 50)

    return () => window.removeEventListener('scroll', updateToTopVisible)
  }, [swiper, toTopVisible])

  const isDesktop = typeof window !== `undefined` && window.innerWidth > 1023
  const isTouchDevice =
    (typeof window !== `undefined` && 'ontouchstart' in window) ||
    (typeof navigator !== `undefined` && navigator.MaxTouchPoints > 0) ||
    (typeof navigator !== `undefined` && navigator.msMaxTouchPoints > 0)

  const params = {
    spaceBetween: 0,
    slidesPerView: 1,
    breakpoints: {
      1023: {
        slidesPerView: 1,
      },
    },
    noSwiping: isDesktop && !isTouchDevice,
    zoom: true,
    getSwiper: updateSwiper,
    preloadImages: false,
  }

  const productAccordionItems = productAccordions?.map(
    ({ title, content, svg, name }) => ({
      title,
      content,
      svg: svg,
    }),
  )

  const getPrice = (slug) => {
    const priceEl = document.querySelector(
      `.add-to-cart .product-price.${slug}`,
    )
    if (priceEl) {
      const amount = Number(priceEl.textContent.replace(currency?.symbol, ''))
      return amount
    } else {
      return null
    }
  }

  useEffect(() => {
    if (product) {
      const originalPrice =
        product.variants.edges[0].node.presentmentPrices.edges[0].node
          .compareAtPrice
      if (originalPrice) {
        setOriginalPrice(
          parseInt(
            product.variants.edges[0].node.presentmentPrices.edges.find(
              ({
                node: {
                  compareAtPrice: { currencyCode },
                },
              }) => currencyCode === currency.code,
            ).node.compareAtPrice.amount,
          ),
        )
        setDiscountPrice(
          parseInt(
            product.variants.edges[0].node.presentmentPrices.edges.find(
              ({
                node: {
                  price: { currencyCode },
                },
              }) => currencyCode === currency.code,
            ).node.price.amount,
          ),
        )
      } else {
        setDiscountPrice(
          parseInt(
            product.variants.edges[0].node.presentmentPrices.edges.find(
              ({
                node: {
                  price: { currencyCode },
                },
              }) => currencyCode === currency.code,
            ).node.price.amount,
          ),
        )
      }
    }
  }, [product])

  return (
    <StyledProductitem>
      <Header>
        <BreadCrumb>
          <SiteLink to=''>{home}</SiteLink>
          {' / '}
          {family?.collection && (
            <>
              <SiteLink to={family.collection[0].slug}>
                {family.collection[0].title}
              </SiteLink>
              {' / '}
            </>
          )}
          {title}
        </BreadCrumb>
        <BreadCrumb>
          {relatedProduct && (
            <SiteLink to={relatedProduct.slug}>{nextProduct}</SiteLink>
          )}
        </BreadCrumb>
      </Header>
      <Photos>
        {!isSkiing && showAnnouncementBarBlackFriday2022 ? (
          <BFLabelContainer>
            <BFLabelOne>
              <RichText json={pDPBlackFridayLabelLineOne2022} />
            </BFLabelOne>
            <BFLabelTwo>{pDPBlackFridayLabelLineTwo2022}</BFLabelTwo>
          </BFLabelContainer>
        ) : (
          <>
            <DynamicLabel
              type={labelTopRight}
              text={labelTopRightText}
              image={labelTopRightImage}
              style={TopRightLabel}
            />
            <DynamicLabel
              type={labelBottomRight}
              text={labelBottomRightText}
              image={labelBottomRightImage}
              style={BottomRightLabel}
            />
            {/* Black Friday */}
            {showAnnouncementBarBlackFriday2022 && originalPrice && (
              <ProductLabelLogoWrapper>
                <ProductLabelLogo className='label-logo' label='Percentage'>
                  -{getDiscountPercentage(discountPrice, originalPrice)}
                </ProductLabelLogo>
              </ProductLabelLogoWrapper>
            )}
            {/* .end */}
          </>
        )}

        <PhotosList>
          {images?.map(({ title, file, fluid }, index) => (
            <PhotosListItem
              key={title + index}
              selected={index === selectedImage || index === swiperIndex}
            >
              <PhotosListButton
                type='button'
                aria-label='select photo'
                onClick={() => {
                  setSwiperIndex(index)
                  setSelectedImage(index)
                  swiper.slideTo(index)
                  useProductInfoClickedTracking({
                    currency: currency?.code,

                    locale: node_locale,
                    name: title,
                    family: family.name,
                    viewType: 'Product Page',
                    productInfoModule: 'Image Thumbnail',
                    linkType: 'Image',
                    imageUrl: `https:${file.url}`,
                  })
                }}
              >
                <Image fluid={fluid} key={index} alt={title} />
              </PhotosListButton>
            </PhotosListItem>
          ))}
        </PhotosList>

        <StyledCarousel>
          <Swiper {...params}>
            {images?.map(({ title: imageTitle, file, fluid }, index) => (
              <Slide
                key={index}
                onClick={() => {
                  setImageZoom({
                    isOpen: true,
                    image: { fluid, imageTitle },
                  })
                  useProductZoomClickedTracking({
                    currency: currency?.code,

                    locale: node_locale,
                    name: title,
                    family: family.name,
                    price: getPrice(productSlug),
                    quantity: 1,
                    imageUrl: `https:${file.url}`,
                    position: index + 1,
                  })
                }}
              >
                <Photo
                  fluid={fluid}
                  alt={imageTitle}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </Slide>
            ))}
          </Swiper>
        </StyledCarousel>
        {imageZoom.isOpen && (
          <ImageZoom
            image={imageZoom.image}
            setImageZoom={setImageZoom}
          ></ImageZoom>
        )}
      </Photos>
      <Info>
        <InfoHeader>
          <Title>{title}</Title>
          {family && family.legacySkus && (
            <ToReviewButton
              type='button'
              onClick={() => {
                onViewReviews()
                useReviewAnchorClickedTracking({
                  currency: currency?.code,

                  locale: node_locale,
                  family: family.name,
                  price: getPrice(productSlug),
                  quantity: 1,
                  name: title,
                })
              }}
            >
              <Reviews productPage legacy_skus={family?.legacySkus} />
            </ToReviewButton>
          )}
          {shortenedDescription && shortenedDescription.shortenedDescription && (
            <button className='text-preview__button' type='button'>
              <p>{shortenedDescription.shortenedDescription}</p>
            </button>
          )}
          {multipackProductInfo && (
            <ProductBulletsSectionDesktop>
              {showProductPageBulletsHeading && (
                <ProductBulletsHeading>
                  {multipackProductInfo.title}
                </ProductBulletsHeading>
              )}
              <ProductBullets>
                {multipackProductInfo.bullets.map((bullet, index) => (
                  <MultipackProductBullet key={bullet + index}>
                    <p>{bullet}</p>
                  </MultipackProductBullet>
                ))}
              </ProductBullets>
            </ProductBulletsSectionDesktop>
          )}
          {productBullets && (
            <ProductBulletsSectionDesktop>
              {showProductPageBulletsHeading && (
                <ProductBulletsHeading>
                  {productBullets.title}
                </ProductBulletsHeading>
              )}
              <ProductBullets>
                {productBullets.bullets.map((bullet, index) => (
                  <ProductBullet key={bullet + index}>
                    <p>{bullet}</p>
                  </ProductBullet>
                ))}
              </ProductBullets>
            </ProductBulletsSectionDesktop>
          )}
        </InfoHeader>
        <ColorPicker>
          <div>
            <span>{colorText}:</span> {color?.title}
          </div>
          {family && (
            <ColorListWrapper>
              {family.product &&
                sortedProductsByName.map(({ color: swatch, slug }, index) => (
                  <ColorListItem
                    key={slug + index}
                    selected={slug === productSlug}
                    dontShow={family.product.length <= 1}
                  >
                    <Color
                      aria-label={`select ${swatch?.title} color`}
                      to={slug}
                      values={swatch?.color}
                      onClick={() =>
                        useColorSelectedTracking({
                          currency: currency?.code,
                          locale: node_locale,
                          label: swatch.title,
                          prevColor: color?.title,
                          destination: `/${node_locale}/${slug}/`,
                        })
                      }
                      state={{ referrer: location.pathname }}
                    />
                  </ColorListItem>
                ))}
            </ColorListWrapper>
          )}
        </ColorPicker>
        {productType && (
          <>
            <SizeTabel
              productType={productType}
              visible={sizeTableOpen}
              gender={gender}
              onSizeTableClose={() => setSizeTableOpen(false)}
            />
            <ProductForm
              name={name}
              title={title}
              location={location}
              handle={shopifyProduct?.handle}
              handle2={shopifyProduct2?.handle}
              shopifyProduct={shopifyProduct}
              sizeSelect={sizeSelect}
              gender={gender}
              color={color}
              productType={productType}
              onSizeTableOpen={() => setSizeTableOpen(true)}
              outOfStockMessages={outOfStockMessages}
              thumbnailImage={thumbnailImage}
              productSlug={productSlug}
              family={family}
              variants={variants}
              setVariants={setVariants}
              selectedVariant={selectedVariant}
              setSelectedVariant={setSelectedVariant}
              sizeIndex={sizeIndex}
              setSizeIndex={setSizeIndex}
              amazonUrl={amazonUrl}
              amazonPriceValue={amazonPriceValue}
              notComingBack={notComingBack}
              sizeInfo={sizeInfo}
              removeIndividualTieredDiscountMessage={
                removeIndividualTieredDiscountMessage
              }
              preorder={preorder}
              preorderMessage={preorderMessage}
            />
          </>
        )}
        {showProductBundle && productBundle && (
          <Bundle productBundle={productBundle} />
        )}
        <AmazonButton
          amazonUrl={amazonUrl}
          amazonPriceValue={amazonPriceValue}
          product={currentProduct}
          title={title}
          variants={variants}
          selectedVariant={selectedVariant}
          sizeIndex={sizeIndex}
        />
        {multipackProductInfo && (
          <ProductBulletsSectionMobile>
            {showProductPageBulletsHeading && (
              <ProductBulletsHeading>
                {multipackProductInfo.title}
              </ProductBulletsHeading>
            )}
            <ProductBullets>
              {multipackProductInfo.bullets.map((bullet, index) => (
                <MultipackProductBullet key={bullet + index}>
                  <p>{bullet}</p>
                </MultipackProductBullet>
              ))}
            </ProductBullets>
          </ProductBulletsSectionMobile>
        )}
        {productBullets && (
          <ProductBulletsSectionMobile>
            {showProductPageBulletsHeading && (
              <ProductBulletsHeading>
                {productBullets.title}
              </ProductBulletsHeading>
            )}
            <ProductBullets>
              {productBullets.bullets.map((bullet, index) => (
                <ProductBullet key={bullet + index}>
                  <p>{bullet}</p>
                </ProductBullet>
              ))}
            </ProductBullets>
          </ProductBulletsSectionMobile>
        )}
        {productAccordions && isMobile && (
          <StyledAccordion
            startClosed
            items={productAccordionItems}
            productInfoTrackingData={{
              currency: currency?.code,

              locale: node_locale,
              family: family.name,
              name: title,
              linkType: 'Button',
              viewType: 'Product Page',
              productInfoModule: 'Product Info Tab',
              // linkCta is mapped in Accordion.js
            }}
          />
        )}
        {productAccordions && !isMobile && (
          <StyledAccordion
            startClosed
            items={productAccordionItems}
            productInfoTrackingData={{
              currency: currency?.code,

              locale: node_locale,
              family: family.name,
              name: title,
              linkType: 'Button',
              viewType: 'Product Page',
              productInfoModule: 'Product Info Tab',
              // linkCta is mapped in Accordion.js
            }}
          />
        )}
      </Info>
      <BackToTop visible={toTopVisible}>
        <BackToTopButton type='button' onClick={onToTop}>
          <div>
            <ToTopTitle>{title}</ToTopTitle>
            {family && (
              <ToTopReviews productPage legacy_skus={family.legacySkus} />
            )}
          </div>
          <ToTopSize>{backToTopText}</ToTopSize>
        </BackToTopButton>
      </BackToTop>
    </StyledProductitem>
  )
}

const BFLabelContainer = styled.div`
  position: absolute;
  top: 5vw;
  right: 5vw;
  display: flex;
  gap: 0.3vw;
  padding: 1.5vw;
  flex-direction: column;
  background-color: #ff1e00;
  color: white;
  z-index: 10;
  text-align: center;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding: 0.6vw;
  }
`

const BFLabelOne = styled.div`
  font-size: 3.6vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: 1.3vw;
  }
`

const BFLabelTwo = styled.div`
  font-size: 1.6vw;
  text-transform: uppercase;
  font-family: AkiraExpanded;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: 0.8vw;
  }
`

const ProductLabelLogoWrapper = styled.div`
  position: absolute;
  top: 3vw;
  right: 3vw;

  .label-logo {
    font-size: ${(props) => props.theme.fontSize.m23};
    line-height: 6vw;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    position: relative;
    top: -1.25vw;
    left: 16vw;
    right: auto;

    .label-logo {
      font-size: 1.15vw;
      line-height: 2vw;
    }
  }
`

const StyledProductitem = styled.section`
  display: flex;
  flex-wrap: wrap;
  background: #f6f6f6;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding: 0 ${(props) => props.theme.space.marginDesktop * 12}vw
      ${(props) => props.theme.space.marginDesktop * 4}vw;
  }
`

const TopRightLabel = css`
  position: absolute;
  font-size: ${mobileVW(10)};
  top: 1em;
  right: 1em;
  z-index: 5;

  @media only screen and (min-width: ${breakPoints.desktop}) {
    font-size: ${desktopVW(20, 1440)};
  }
`

const BottomRightLabel = css`
  position: absolute;
  font-size: ${mobileVW(10)};
  bottom: 1em;
  right: 1em;
  z-index: 5;

  @media only screen and (min-width: ${breakPoints.desktop}) {
    font-size: ${desktopVW(20, 1440)};
  }
`

const Header = styled.div`
  display: none;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: flex;
    justify-content: space-between;
    width: 100%;
    color: #a2a2a2;
    font-size: ${(props) => props.theme.fontSize.d12};
    padding: ${(props) => props.theme.space.marginDesktop * 4}vw 0
      ${(props) => props.theme.space.marginDesktop}vw;
  }
`

const Photos = styled.div`
  background: #f6f6f6;
  min-height: 93vw;
  position: relative;
  flex: 100%;
  display: flex;
  padding-top: ${(props) => props.theme.space.marginMobile / 2}vw;
  padding-right: ${(props) => props.theme.space.marginMobile * 2}vw;
  padding-bottom: ${(props) => props.theme.space.marginMobile / 2}vw;
  padding-left: ${(props) => props.theme.space.marginMobile}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: 45vw;
    min-height: auto;
    flex: 6;
    padding-top: ${(props) => props.theme.space.marginDesktop * 2}vw;
    padding-right: ${(props) => props.theme.space.marginDesktop * 2}vw;
    padding-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
    padding-left: 0;
  }
`
const PhotosList = styled.ul`
  flex: 1;
  list-style: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`
const PhotosListItem = styled.li`
  border: 1px solid;
  border-color: ${(props) => (props.selected ? 'white' : 'transparent')};
`

const PhotosListButton = styled.button`
  outline: none;
  background: none;
  width: ${(props) => props.theme.space.marginMobile * 4}vw;
  height: ${(props) => props.theme.space.marginMobile * 5}vw;
  margin-bottom: ${(props) => props.theme.space.marginMobile};
  cursor: pointer;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    width: ${(props) => props.theme.space.marginDesktop * 10}vw;
    height: ${(props) => props.theme.space.marginDesktop * 12}vw;
    margin-bottom: ${(props) => props.theme.space.marginDesktop}vw;
  }
`

const StyledCarousel = styled.div`
  flex: 5;
  width: 71.5vw;
  height: auto;
  overflow: hidden;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    width: 39.5vw;
  }

  .swiper-container {
    height: 100%;
    width: 100%;
  }
  .swiper-container-horizontal > .swiper-scrollbar {
    display: none;
  }
`

const Slide = styled.div`
  display: block;
  width: 100%;
  height: 100%;
  position: relative;
`

const Photo = styled(Image)`
  height: 100%;
  width: 100%;
  cursor: zoom-in;
`

const Info = styled.div`
  flex: 100%;
  display: flex;
  flex-direction: column;
  background: white;
  padding-top: ${(props) => props.theme.space.marginMobile}vw;
  padding-right: ${(props) => props.theme.space.marginMobile}vw;
  padding-left: ${(props) => props.theme.space.marginMobile}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    background: #f6f6f6;
    flex: 4;
    padding-top: 3.5vw;
    padding-right: ${(props) => props.theme.space.marginDesktop * 2}vw;
    padding-left: ${(props) => props.theme.space.marginDesktop * 2}vw;
  }
`

const InfoHeader = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: space-between;
  padding-bottom: ${(props) => props.theme.space.marginMobile}vw;

  .text-preview__button {
    text-align: left;
    display: block;

    p {
      width: 100%;
      margin: 0 0 3vw;
      line-height: 1.6;
      font-size: ${(props) => props.theme.fontSize.m26};

      span {
        text-decoration: underline;
        margin-left: 0.75vw;
      }
    }
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: block;
    padding-bottom: 0;

    .text-preview__button {
      p {
        margin: 0 0 1.6vw;
        line-height: 1.7;
        font-size: ${(props) => props.theme.fontSize.d14};

        span {
          margin-left: 0.15vw;
        }
      }
    }
  }
`

const Title = styled.h1`
  font-size: ${(props) => props.theme.fontSize.m35};
  line-height: 6vw;
  padding-right: ${(props) => props.theme.space.marginMobile}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d30};
    line-height: 2.77778vw;
    padding-bottom: ${(props) => props.theme.space.marginDesktop}vw;
  }
`
const ToReviewButton = styled.button`
  height: ${mobileVW(30)};

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: 2vw;
  }
`

const Reviews = styled(ReviewStarsProduct)`
  display: block;
  text-decoration: underline;

  svg {
    width: 4vw;
  }

  @media screen and (min-width: ${breakPoints.desktop}) {
    svg {
      width: 1.035vw;
    }
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    display: none;
  }
`

const ColorPicker = styled.div`
  font-size: ${(props) => props.theme.fontSize.m23};
  display: flex;
  flex-wrap: wrap;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  align-items: center;
  padding-bottom: 3vw;

  div {
    padding-bottom: 3vw;
    width: 100%;
  }

  span {
    font-family: ${(props) => props.theme.fontFamily.bold};
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    padding-bottom: 1.7vw;

    > div {
      padding-bottom: 0;
      width: 100%;
    }
  }
`
const ColorListWrapper = styled.ul`
  display: flex;
  min-width: 100%;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    min-width: 0;
  }
`
const ColorListItem = styled.li`
  position: relative;
  flex: 0 1 12vw;
  border: 1px solid;
  border-color: ${(props) =>
    props.selected ? props.theme.colors.mediumGrey : 'transparent'};
  border-radius: 100%;
  display: ${(props) => (props.dontShow ? 'none' : 'inline-block')};
  margin-top: 0;
  &:after {
    content: '';
    width: 100%;
    padding-top: 100%;
    display: block;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin-top: ${(props) => props.theme.space.marginDesktop}vw;
    margin-left: ${(props) => props.theme.space.marginDesktop}vw;
    height: ${(props) => props.theme.space.marginDesktop * 3}vw;
    width: ${(props) => props.theme.space.marginDesktop * 3}vw;
  }
`

const Color = styled(SiteLink)`
  display: block;
  top: 50%;
  left: 50%;

  border-radius: 100%;
  background-color: ${(props) => `#${props.values?.[0]}`};
  position: absolute;
  transform: translate(-50%, -50%) rotate(-135deg);

  height: 75%;
  width: 75%;

  &::after {
    content: '';
    height: 100%;
    width: 50%;
    display: block;
    background-color: ${(props) => `#${props.values?.[1]}`};
    border-radius: ${(props) => props.theme.space.marginMobile * 2}vw 0 0
      ${(props) => props.theme.space.marginMobile * 2}vw;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: 1.52vw;
    width: 1.52vw;
  }
`

const NeverInStock = styled(SiteLink)`
  color: black;
  background: transparent;
  border: 1px solid black;
  flex: 2;
  cursor: pointer;
  font-size: ${(props) => props.theme.fontSize.m26};
  letter-spacing: 0.05em;
  height: ${(props) => props.theme.space.marginMobile * 4}vw;
  min-width: 92vw;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d14};
    height: 4.2vw;
    min-width: 28vw;
  }
`

const Text = styled.div`
  display: ${(props) => (props.visible ? 'inline-block' : 'none')};

  span {
    font-family: ${(props) => props.theme.fontFamily.medium};
  }
`

const Loader = styled.div`
  display: ${(props) => (props.visible ? 'inline-block' : 'none')};
  position: relative;
  width: 10vw;
  height: 10vw;

  div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 8vw;
    height: 8vw;
    margin: 1vw 0;
    border: 1vw solid #fff;
    border-radius: 50%;
    animation: rotate 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: #fff transparent transparent transparent;

    &:nth-child(1) {
      animation-delay: -0.45s;
    }
    &:nth-child(2) {
      animation-delay: -0.3s;
    }
    &:nth-child(3) {
      animation-delay: -0.15s;
    }

    @keyframes rotate {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    width: 2.5vw;
    height: 2.5vw;

    div {
      box-sizing: border-box;
      display: block;
      position: absolute;
      width: 2.1vw;
      height: 2.1vw;
      margin: 0.3vw 0;
      border: 0.2vw solid #fff;
      border-color: #fff transparent transparent transparent;
    }
  }
`

const StyledAccordion = styled(Accordion)`
  color: ${(props) => props.theme.colors.darkerGrey};
  font-size: ${(props) => props.theme.fontSize.m23};
  a {
    text-decoration: underline;
  }
  p {
    line-height: 5vw;
    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      line-height: ${(props) => props.theme.fontSize.d20};
    }
  }
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
  }
  button {
    display: flex;
    align-items: center;
    text-transform: uppercase;
    font-size: ${(props) => props.theme.fontSize.m26};
    font-weight: bold;
    position: relative;
    width: 100%;
    color: ${(props) => props.theme.colors.darkerGrey};
    padding-top: ${(props) => props.theme.space.marginMobile}vw;
    padding-bottom: ${(props) => props.theme.space.marginMobile}vw;
    svg {
      fill: ${(props) => props.theme.colors.darkerGrey};
    }
    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      font-size: ${(props) => props.theme.fontSize.d12};
      padding-top: ${(props) => props.theme.space.marginDesktop}vw;
      padding-bottom: ${(props) => props.theme.space.marginDesktop}vw;
    }

    &::after {
      content: '';
      border: solid ${(props) => props.theme.colors.darkerGrey};
      border-width: 0 0.4vw 0.4vw 0;
      display: block;
      width: 1.8vw;
      height: 1.8vw;
      transform: rotate(45deg);
      position: absolute;
      right: ${(props) => props.theme.space.marginMobile / 5}vw;
      top: ${(props) => props.theme.space.marginMobile}vw;
      @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
        border-width: 0 ${(props) => props.theme.space.marginDesktop / 5}vw
          ${(props) => props.theme.space.marginDesktop / 5}vw 0;
        width: ${(props) => props.theme.space.marginDesktop / 2}vw;
        height: ${(props) => props.theme.space.marginDesktop / 2}vw;
        top: ${(props) => props.theme.space.marginDesktop}vw;
      }
    }

    &.active::after {
      transform: rotate(-135deg);
    }
  }

  li {
    border-bottom: 1px solid #e5e5e5;

    &:last-child {
      border-bottom: none;
    }
    div {
      padding-bottom: ${(props) => props.theme.space.marginMobile * 0.75}vw;
      @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
        padding-bottom: ${(props) => props.theme.space.marginDesktop * 1.5}vw;
      }
      ul {
        li {
          border: none;
          display: flex;
          justify-content: flex-start;
          align-items: center;
          margin-bottom: ${(props) => props.theme.space.marginMobile * 0.6}vw;
          line-height: 7vw;
          @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
            &:last-child {
              margin-bottom: 0;
            }
            margin-bottom: ${(props) => props.theme.space.marginDesktop}vw;
            line-height: ${(props) => props.theme.fontSize.d35};
          }
          a {
            width: auto !important;
            height: 7vw;
            @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
              height: 2vw;
            }
          }
          &::before {
            position: absolute;
            content: '';
            display: block;
            display: inline-block;
            width: ${(props) => props.theme.space.marginMobile / 4}vw;
            height: ${(props) => props.theme.space.marginMobile / 4}vw;
            border: solid ${(props) => props.theme.colors.darkerGrey};
            border-radius: 50%;
            border-width: 1px;
            transform: rotate(45deg);
            @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
              width: ${(props) => props.theme.space.marginDesktop / 2}vw;
              height: ${(props) => props.theme.space.marginDesktop / 2}vw;
            }
          }
          p {
            display: inline;
            line-height: 5vw;
            margin-left: ${(props) => props.theme.space.marginMobile}vw;
            @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
              line-height: ${(props) => props.theme.fontSize.d20};
              margin-left: ${(props) =>
                props.theme.space.marginDesktop * 1.5}vw;
            }
          }
          img {
            display: inline;
            margin-left: ${(props) =>
              props.theme.space.marginMobile / 2}vw !important;
            margin-right: ${(props) =>
              props.theme.space.marginMobile / 2}vw !important;
            width: auto !important;
            height: 7vw;
            @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
              height: 2vw;
              margin-left: ${(props) =>
                props.theme.space.marginDesktop}vw !important;
              margin-right: ${(props) =>
                props.theme.space.marginDesktop}vw !important;
            }
          }
        }
      }
    }
  }
`

const ProductBulletsSectionMobile = styled.div`
  display: block;
  padding-left: ${(props) => props.theme.space.marginMobile / 5}vw;
  padding-bottom: ${(props) => props.theme.space.marginMobile * 1.3}vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: none;
  }
`

const ProductBulletsSectionDesktop = styled.div`
  display: none;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: block;
    padding-bottom: ${(props) => props.theme.space.marginDesktop * 1.3}vw;
    padding-left: ${(props) => props.theme.space.marginDesktop / 3}vw;
  }
`

const ProductBulletsHeading = styled.h3`
  font-size: ${(props) => props.theme.fontSize.m26};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: bold;
  margin-left: -${(props) => props.theme.space.marginMobile / 4}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    margin-left: -${(props) => props.theme.space.marginDesktop / 4}vw;
  }
`

const ProductBullets = styled.ul`
  display: block;
  font-size: ${(props) => props.theme.fontSize.m23};
  padding-top: ${(props) => props.theme.space.marginMobile / 1.5}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    color: ${(props) => props.theme.colors.darkGrey};
    font-size: ${(props) => props.theme.fontSize.d12};
    padding-top: ${(props) => props.theme.space.marginDesktop * 1.2}vw;
    margin-left: -${(props) => props.theme.space.marginDesktop / 5}vw;
  }
`

const ProductBullet = styled.li`
  display: flex;
  align-items: center;
  padding-bottom: ${(props) => props.theme.space.marginMobile / 2}vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding-bottom: ${(props) => props.theme.space.marginDesktop}vw;
  }

  p {
    margin-left: ${(props) => props.theme.space.marginMobile}vw;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      margin-left: ${(props) => props.theme.space.marginDesktop}vw;
    }
  }

  &::before {
    position: absolute;
    content: '';
    display: block;
    display: inline-block;
    width: ${(props) => props.theme.space.marginMobile / 4}vw;
    height: ${(props) => props.theme.space.marginMobile / 4}vw;
    border: solid #000;
    border-radius: 50%;
    border-width: 1px;
    transform: rotate(45deg);

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      width: ${(props) => props.theme.space.marginDesktop / 3}vw;
      height: ${(props) => props.theme.space.marginDesktop / 3}vw;
    }
  }
`
const MultipackProductBullet = styled.li`
  display: flex;
  align-items: center;
  padding-bottom: ${(props) => props.theme.space.marginMobile / 2}vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding-bottom: ${(props) => props.theme.space.marginDesktop}vw;
  }
`

const UspSection = styled.div`
  display: block;
  padding-left: ${(props) => props.theme.space.marginMobile / 5}vw;
  padding-bottom: ${(props) => props.theme.space.marginMobile * 2}vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding-bottom: ${(props) => props.theme.space.marginDesktop * 3}vw;
    padding-left: ${(props) => props.theme.space.marginDesktop / 5}vw;
  }
`

const UspHeading = styled.h3`
  font-size: ${(props) => props.theme.fontSize.m26};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: bold;
  margin-left: -${(props) => props.theme.space.marginMobile / 4}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    margin-left: -${(props) => props.theme.space.marginDesktop / 4}vw;
  }
`

const Usps = styled.ul`
  display: block;
  font-size: ${(props) => props.theme.fontSize.m23};
  padding-top: ${(props) => props.theme.space.marginMobile / 1.5}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    color: ${(props) => props.theme.colors.darkGrey};
    font-size: ${(props) => props.theme.fontSize.d11};
    padding-top: ${(props) => props.theme.space.marginDesktop * 1.2}vw;
  }
`
const Usp = styled.li`
  display: flex;
  align-items: center;
  padding-bottom: ${(props) => props.theme.space.marginMobile / 2}vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding-bottom: ${(props) => props.theme.space.marginDesktop}vw;
  }

  p {
    margin-left: ${(props) => props.theme.space.marginMobile}vw;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      margin-left: ${(props) => props.theme.space.marginDesktop}vw;
    }
  }

  &::before {
    content: '';
    display: block;
    display: inline-block;
    width: ${(props) => props.theme.space.marginMobile / 4}vw;
    height: ${(props) => props.theme.space.marginMobile / 2}vw;
    border: solid #000;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      width: ${(props) => props.theme.space.marginDesktop / 4}vw;
      height: ${(props) => props.theme.space.marginDesktop / 2}vw;
    }
  }
`

const ProductLabel = styled.div`
  background: ${(props) =>
    props.new ||
    props.comingSoon ||
    props.limitedEdition ||
    props.twoPack ||
    props.specialEdition ||
    props.giftWrapping
      ? props.theme.colors.yellow
      : props.theme.colors.blue};
  font-family: ${(props) => props.theme.fontFamily.normal};
  position: absolute;
  color: ${(props) =>
    props.new ||
    props.comingSoon ||
    props.limitedEdition ||
    props.twoPack ||
    props.specialEdition ||
    props.giftWrapping
      ? props.theme.colors.blue
      : props.theme.colors.white};
  z-index: 2;
  top: 8vw;
  right: 8vw;
  padding: 0 ${mobileVW(13)};
  line-height: 5.5vw;
  font-size: ${(props) => props.theme.fontSize.m18};
  text-align: center;
  border-radius: 10vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    top: 3vw;
    left: 10vw;
    right: auto;
    font-size: ${(props) => props.theme.fontSize.d12};
    padding: 0 ${desktopVW(25)};
    line-height: 2.1vw;
  }
`

const BackToTop = styled.div`
  transform: ${(props) =>
    props.visible ? `translateY(0)` : `translateY(calc(100% + 6vw))`};
  transition: transform 0.2s ease-in;
  z-index: 2;
  position: fixed;
  right: 2vw;
  bottom: 6vw;
  left: 2vw;
  padding: 2vw;
  background-color: white;
  box-shadow: 0 0 0.6rem 0 rgba(0, 0, 0, 0.22);

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: none;
  }
`

const BackToTopButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: stretch;
`

const ToTopTitle = styled.h2`
  font-size: 4vw;
  padding: 0.5vw 0 2.25vw 0.5vw;
  letter-spacing: 0.04em;
  text-align: left;
`

const ToTopReviews = styled(ReviewStarsProduct)`
  margin: 0 0 1vw 0.75vw;
`

const ToTopSize = styled.div`
  background-color: ${(props) => props.theme.colors.blue};
  color: ${(props) => props.theme.colors.white};
  font-size: 3vw;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0 5vw;
  line-height: 1.2;
`

const ProductLogoWrapper = styled.div`
  position: absolute;
  width: 95%;
  display: flex;
  justify-content: flex-end;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    width: 95%;
  }
`

export default ProductItem
